/**
 * AuditFounding.js — /audit/founding
 * v2 — Post-purchase page. Navy/Teal/Green scheme matching landing page v2.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API   = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY  = "#0F2D52";
const TEAL  = "#0B6E6E";
const GREEN = "#1A6B3A";

export default function AuditFounding() {
  const [searchParams]  = useSearchParams();
  const sessionId       = searchParams.get("session_id") || localStorage.getItem("quiz_session_id");
  const paymentIntentId = searchParams.get("payment_intent_id");

  const [reportReady, setReportReady]     = useState(false);
  const [firstName, setFirstName]         = useState("");
  const [foundingSpots, setFoundingSpots] = useState(null);
  const [confirming, setConfirming]       = useState(true);

  // Confirm payment → triggers report generation
  useEffect(() => {
    if (!paymentIntentId || !sessionId) { setConfirming(false); return; }
    (async () => {
      try {
        await axios.post(`${API}/quiz/confirm-purchase`, null, {
          params: { payment_intent_id: paymentIntentId, quiz_session_id: sessionId }
        });
      } catch { /* non-fatal */ }
      finally { setConfirming(false); }
    })();
  }, [paymentIntentId, sessionId]);

  // Fetch session name
  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/quiz/${sessionId}/status`)
      .then(r => { if (r.data.first_name) setFirstName(r.data.first_name); })
      .catch(() => {});
  }, [sessionId]);

  // Fetch founding spots remaining
  useEffect(() => {
    axios.get(`${API}/quiz/pricing`)
      .then(r => setFoundingSpots(r.data.spots_remaining ?? null))
      .catch(() => {});
  }, []);

  // Poll until report_generated
  useEffect(() => {
    if (!sessionId || reportReady) return;
    let attempts = 0;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/quiz/${sessionId}/status`);
        if (res.data.status === "report_generated" || res.data.email_sent_at) {
          setReportReady(true);
          clearInterval(poll);
        }
        attempts++;
        if (attempts > 36) clearInterval(poll); // 90s max
      } catch { clearInterval(poll); }
    }, 2500);
    return () => clearInterval(poll);
  }, [sessionId, reportReady]);

  const displayName = firstName || "there";

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  minHeight: "100vh", background: NAVY }}>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-8px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Nav */}
      <nav style={{ background: NAVY, padding: "14px 24px",
                    borderBottom: "1px solid #1a3d6b",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontWeight: 800, fontSize: 18, color: "#6ee7b7" }}>SolelyTax AI</span>
      </nav>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "52px 20px 72px" }}>

        {/* ── REPORT STATUS ───────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", marginBottom: 40,
                      animation: "fadeIn 0.5s ease" }}>

          {/* Status icon */}
          <div style={{ fontSize: 56, marginBottom: 14 }}>
            {reportReady ? "✅" : confirming ? "🔐" : "⏳"}
          </div>

          <h1 style={{ fontSize: "clamp(22px,5vw,34px)", fontWeight: 900,
                        color: "#f1f5f9", lineHeight: 1.2, marginBottom: 12,
                        letterSpacing: "-0.02em" }}>
            {reportReady
              ? "Your Report Is On Its Way."
              : confirming
                ? "Confirming your payment…"
                : "Your Report Is Being Generated."}
          </h1>

          <p style={{ color: "#94a3b8", fontSize: 16, lineHeight: 1.75, marginBottom: 12 }}>
            {reportReady
              ? `Check your inbox, ${displayName} — your personalized CPA Readiness Report just landed.`
              : `Your 15–20 page personalized PDF will be delivered to your inbox within a few minutes, ${displayName}.`}
          </p>

          {/* Download CTA (shows when ready) */}
          {reportReady && sessionId && (
            <a
              href={`${API}/quiz/${sessionId}/download`}
              target="_blank" rel="noopener noreferrer"
              data-testid="founding-download-btn"
              style={{ display: "inline-block", background: GREEN, color: "#fff",
                       textDecoration: "none", borderRadius: 6,
                       padding: "13px 28px", fontWeight: 700, fontSize: 15, marginTop: 8 }}>
              Download PDF Now →
            </a>
          )}

          {/* Animated waiting dots */}
          {!reportReady && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 9, height: 9, borderRadius: "50%", background: "#6ee7b7",
                  animation: `bounce 1.2s ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
          )}
        </div>

        {/* ── BRIDGE COPY ─────────────────────────────────────────────────── */}
        <div style={{ background: "#1a3d6b", border: "1px solid #2a5280",
                      borderLeft: `4px solid ${TEAL}`,
                      borderRadius: 4, padding: "24px 28px", marginBottom: 28 }}>
          <p style={{ color: "#e2e8f0", fontSize: 16, fontWeight: 700,
                      lineHeight: 1.6, marginBottom: 10 }}>
            While you wait — one more thing.
          </p>
          <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.85, marginBottom: 10 }}>
            Your report shows you <strong style={{ color: "#e2e8f0" }}>what's missing</strong>.
            SolelyTax AI fixes it — and keeps you ready for every CPA conversation, year-round.
          </p>
          <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.85, margin: 0 }}>
            Income organized. Deductions tracked. Tax position visible at every moment.
            CPA-ready exports generated automatically. Not a one-time audit — a system.
          </p>
        </div>

        {/* ── FOUNDING OFFER ──────────────────────────────────────────────── */}
        <div style={{ background: "#fff", border: `2px solid ${GREEN}`,
                      borderRadius: 4, padding: 28, marginBottom: 28 }}>

          <div style={{ fontSize: 11, fontWeight: 700, color: TEAL,
                        letterSpacing: "0.13em", textTransform: "uppercase", marginBottom: 10 }}>
            Exclusive Offer for Audit Buyers
          </div>

          <h2 style={{ fontSize: "clamp(18px,3.5vw,26px)", fontWeight: 900,
                        color: NAVY, lineHeight: 1.25, marginBottom: 12 }}>
            As a buyer of this audit, you qualify for founding member access:
          </h2>

          <div style={{ fontSize: 48, fontWeight: 900, color: GREEN,
                        lineHeight: 1.1, marginBottom: 4 }}>
            $79/year
          </div>
          <div style={{ color: "#57534e", fontSize: 14, marginBottom: 18 }}>
            Locked forever. After 100 founding members: $19/month or $149/year.
          </div>

          {/* Scarcity if spots known */}
          {foundingSpots !== null && foundingSpots < 100 && (
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa",
                          borderLeft: `3px solid #ea580c`,
                          borderRadius: 4, padding: "10px 14px", marginBottom: 18,
                          color: "#92400e", fontSize: 13, fontWeight: 700 }}>
              ⚡ {foundingSpots} founding spots remaining
            </div>
          )}

          {/* Benefits */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {[
              "Full SolelyTax AI platform access — all features unlocked",
              "$79/year price locked permanently — never increases",
              "Direct line to Alexis — founding member Slack channel",
              "Your feedback shapes what we build next",
              "Founding member badge on your account",
            ].map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ color: GREEN, fontSize: 15 }}>✅</span>
                <span style={{ fontSize: 14, color: "#374151" }}>{b}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <a
            href="/waitlist/founding"
            data-testid="founding-claim-btn"
            style={{ display: "block", background: GREEN, color: "#fff",
                     textDecoration: "none", borderRadius: 6,
                     padding: "16px", fontSize: 17, fontWeight: 700,
                     textAlign: "center", transition: "background 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#145c30"; }}
            onMouseLeave={e => { e.currentTarget.style.background = GREEN; }}>
            Claim My Founding Spot — $79/year →
          </a>

          <div style={{ textAlign: "center", marginTop: 12,
                        color: "#6b7280", fontSize: 13 }}>
            Secure checkout · Cancel anytime
          </div>
        </div>

        {/* ── SECONDARY ───────────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", color: "#64748b", fontSize: 14, lineHeight: 1.9 }}>
          <p>Check your email — your report is arriving now.</p>
          <p>The founding offer will also appear in your email sequence over the next 12 days.</p>
          <p style={{ color: "#475569", fontStyle: "italic", marginTop: 4 }}>
            No pressure. The report is valuable on its own.
          </p>
        </div>

      </div>
    </div>
  );
}
